import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  BadgeCheck, Siren, Phone, ArrowRight, ShieldCheck, Clock, HeartHandshake,
  Church, GraduationCap, Cross, ChevronDown,
} from "lucide-react";
import Marquee from "@/components/Marquee";
import CompareSlider from "@/components/CompareSlider";
import { Reveal, MaskedLines, Eyebrow, SectionHeading, GoldButton, GhostButton, CTABand, SERVICE_ICONS, usePageMeta } from "@/components/kit";
import { SERVICES, COMPARE_PAIRS, HERO_BG, HEADER_SUNSET, PHONE, PHONE_TEL, HOURS_WEEK, HOURS_TIME, SLOGAN } from "@/lib/site";

const HERO_SNAPSHOTS = SERVICES.map((s) => s.image);

export default function HomePage() {
  usePageMeta(
    "Security System Service & Repair | Redeemer Solutions LLC",
    "Redeemer Solutions LLC services and repairs security cameras, access control, fiber optics, Ethernet, and video intercoms in Christiana, TN. There's always a solution."
  );
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const [shotIdx, setShotIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setShotIdx((i) => (i + 1) % HERO_SNAPSHOTS.length), 5500);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="home-page">
      <section ref={heroRef} data-testid="home-hero" className="relative min-h-[100svh] flex items-center overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={HERO_BG} alt="Dramatic low-light security and surveillance technology" className="w-full h-[125%] object-cover opacity-45" loading="eager" />
          <img src={HEADER_SUNSET} alt="" aria-hidden="true" className="absolute inset-0 w-full h-[125%] object-cover object-top opacity-[0.25]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/70 via-[#0A0A0C]/55 to-[#0A0A0C]" />
        </motion.div>
        <div className="absolute inset-0 spotlight pointer-events-none" />
        <motion.div style={{ opacity: fade }} className="relative w-full max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-24">
          <motion.div
            initial={{ opacity: 0, x: "-55vw" }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.4, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="mb-7 relative flex justify-center"
          >
            <div aria-hidden="true" data-testid="hero-logo-slideshow" className="absolute inset-0 -inset-y-8 flex items-center justify-center pointer-events-none">
              {HERO_SNAPSHOTS.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt=""
                  loading="lazy"
                  className={`absolute h-full w-auto max-w-none rounded-sm blur-[1px] transition-opacity duration-[2000ms] [mask-image:radial-gradient(ellipse_70%_70%_at_center,black_62%,transparent_100%)] ${i === shotIdx ? "opacity-[0.38]" : "opacity-0"}`}
                />
              ))}
            </div>
            <span className="relative inline-block rounded-sm hero-logo-glow">
              <img
                src="/logo.png"
                alt="Redeemer Solutions, LLC — There's always a solution."
                data-testid="hero-logo"
                className="h-44 sm:h-60 lg:h-72 w-auto"
              />
            </span>
          </motion.div>
          <p data-testid="hero-slogan-sequence" aria-label="There's Always a Solution." className="font-display text-2xl sm:text-3xl lg:text-4xl text-[#F4EFE6] tracking-wide mb-7">
            {["There's", "Always", "a", "Solution."].map((w, i) => (
              <motion.span
                key={w}
                className="inline-block mr-[0.28em]"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: [0, 1, 1, 0], y: [10, 0, 0, -6] }}
                transition={{ duration: 2.3, delay: 1.35 + i * 0.18, times: [0, 0.22, 0.68, 1], ease: "easeOut" }}
              >
                {w}
              </motion.span>
            ))}
          </p>
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 3.45 }}
            data-testid="isnetworld-credential-badge"
            className="inline-flex items-center gap-2 border border-gold/30 bg-gold/5 rounded-full px-4 py-1.5 mb-7"
          >
            <BadgeCheck size={14} className="text-gold" />
            <span className="font-mono-label text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-gold-champagne">
              ISNetworld Member Contractor
            </span>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 3.5 }}>
            <Eyebrow className="mb-5">Faith-Driven Security Service & Repair · Christiana, Tennessee</Eyebrow>
          </motion.div>
          <MaskedLines
            baseDelay={3.6}
            lines={["Service & Repair,", { t: "Done Right.", gold: true }]}
            className="font-display font-bold text-[11.5vw] sm:text-7xl lg:text-8xl leading-[1.02] tracking-tight text-[#F4EFE6]"
          />
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 3.95 }}
            className="text-zinc-300 mt-7 max-w-2xl text-base sm:text-lg leading-relaxed"
          >
            Security cameras, access control, fiber optics, Ethernet, and video intercom — serviced, repaired, and
            maintained with integrity, backed by after-hours emergency response across Middle Tennessee.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 4.15 }}
            className="flex flex-col sm:flex-row gap-4 mt-10"
          >
            <GoldButton to="/request-quote" testid="hero-request-quote-button">
              Request a Free Quote <ArrowRight size={16} />
            </GoldButton>
            <GhostButton href={PHONE_TEL} testid="hero-call-button">
              <Phone size={16} /> {PHONE}
            </GhostButton>
            <a
              href={PHONE_TEL}
              data-testid="hero-emergency-call-button"
              className="inline-flex items-center justify-center gap-2.5 border border-red-500/40 text-red-400 font-medium text-sm tracking-wide px-7 py-3.5 rounded-sm transition-colors duration-300 hover:bg-red-500/10"
            >
              <Siren size={16} /> 24/7 Emergency
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 4.4 }}
            className="mt-16 flex flex-wrap gap-x-10 gap-y-3 font-mono-label text-xs font-medium uppercase tracking-[0.18em] text-[#F4EFE6] border border-gold/30 bg-[#0A0A0C]/80 backdrop-blur-md rounded-sm px-5 py-4"
          >
            <span data-testid="hero-hours">Open {HOURS_WEEK} · {HOURS_TIME}</span>
            <span className="text-gold-champagne">After-Hours Emergency Dispatch</span>
            <span>Churches · Schools · First Responder Facilities</span>
          </motion.div>
        </motion.div>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gold/60"
        >
          <ChevronDown size={22} />
        </motion.div>
      </section>

      <Marquee />

      <section data-testid="services-section" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <SectionHeading
          eyebrow="What We Do"
          title="Five specialties. One accountable team."
          sub="From the camera on the wall to the fiber in the ground — we service, repair, and maintain the systems that protect and connect your facility."
          testid="services-section-heading"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {SERVICES.map((s, i) => {
            const Icon = SERVICE_ICONS[s.icon];
            return (
              <Reveal key={s.slug} delay={i * 0.08}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group block bg-[#121216] border border-gold/15 rounded-sm overflow-hidden gold-glow-card h-full"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.imageAlt}
                      loading="lazy"
                      className="w-full h-full object-cover opacity-75 transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121216] via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 w-10 h-10 flex items-center justify-center border border-gold/40 bg-[#0A0A0C]/80 backdrop-blur-sm">
                      <Icon size={18} className="text-gold" />
                    </div>
                  </div>
                  <div className="p-6 sm:p-7">
                    <h3 className="font-display text-lg sm:text-xl font-semibold text-gold-champagne/95 leading-snug">{s.name}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed mt-3">{s.short}</p>
                    <span className="inline-flex items-center gap-2 text-gold text-xs font-mono-label uppercase tracking-[0.2em] mt-5 group-hover:gap-3 transition-all">
                      Explore <ArrowRight size={13} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
          <Reveal delay={0.4}>
            <Link
              to="/request-quote"
              data-testid="services-quote-card"
              className="group flex flex-col justify-between bg-gradient-to-br from-[#1A1A22] to-[#121216] border border-gold/25 rounded-sm p-6 sm:p-7 gold-glow-card h-full min-h-[280px]"
            >
              <div>
                <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-gold">Free Estimate</p>
                <h3 className="font-display text-2xl font-semibold text-[#F4EFE6] mt-4 leading-snug">System down or acting up?</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mt-3">
                  Tell us what's happening. We'll diagnose the real fault and give you an honest quote — no pressure, no padding.
                </p>
              </div>
              <span className="inline-flex items-center gap-2 text-gold text-xs font-mono-label uppercase tracking-[0.2em] mt-6 group-hover:gap-3 transition-all">
                Start Your Quote <ArrowRight size={13} />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      <section data-testid="before-after-teaser" className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionHeading
            eyebrow="Proof, Not Promises"
            title="Drag the line. See the difference."
            sub="A real network cabinet clean-up at Calvary Baptist Church in Shelbyville — tangled and unlabeled on the left, dressed and documented on the right. This is what we leave behind."
            testid="before-after-teaser-heading"
          />
          <Reveal delay={0.15}>
            <div className="mt-9">
              <GhostButton to="/gallery" testid="teaser-gallery-button">
                See the Full Gallery <ArrowRight size={15} />
              </GhostButton>
              <p data-testid="gallery-more-soon" className="mt-4 text-sm text-gold-champagne/80 italic">More pictures coming soon.</p>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <CompareSlider
            before={COMPARE_PAIRS[0].before}
            after={COMPARE_PAIRS[0].after}
            title={COMPARE_PAIRS[0].title}
            testid="home-compare-cabinet"
          />
          <p className="text-xs text-zinc-500 mt-4 text-right">{COMPARE_PAIRS[0].location}</p>
        </Reveal>
      </section>

      <section data-testid="values-section" className="border-y border-gold/15 bg-[#0D0D10] relative overflow-hidden">
        <div className="absolute inset-0 spotlight pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionHeading
              eyebrow="Why Redeemer"
              title="Built on faith. Proven in the field."
              sub="We named this company after what we believe. That means the price we quote is the price you pay, the work is done as if for the Lord, and we answer the phone when others won't."
              testid="values-section-heading"
            />
            <Reveal delay={0.15}>
              <blockquote className="mt-10 border-l-2 border-gold/50 pl-6">
                <p className="font-display text-lg sm:text-xl text-gold-champagne/90 italic leading-relaxed">
                  "Whatever you do, work at it with all your heart, as working for the Lord and not for men."
                </p>
                <cite className="font-mono-label text-[11px] uppercase tracking-[0.24em] text-zinc-500 mt-3 block not-italic">Colossians 3:23</cite>
              </blockquote>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              { icon: ShieldCheck, t: "ISNetworld Member", d: "Vetted, safety-verified member contractor trusted by industrial and commercial clients." },
              { icon: Clock, t: "After-Hours Emergency", d: "Systems don't fail on a schedule. Our emergency dispatch answers after hours and weekends." },
              { icon: HeartHandshake, t: "Preferred Rates", d: "Special pricing for churches, schools, and emergency personnel facilities." },
              { icon: Cross, t: "Faith-Driven Integrity", d: "Honest diagnostics and clean workmanship — we treat your property like our own sanctuary." },
            ].map((v, i) => (
              <Reveal key={v.t} delay={i * 0.08}>
                <div className="bg-[#121216] border border-gold/15 rounded-sm p-6 gold-glow-card h-full">
                  <v.icon size={22} className="text-gold" />
                  <h3 className="font-display text-base font-semibold text-[#F4EFE6] mt-4">{v.t}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed mt-2">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="special-rates-section" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <SectionHeading
          eyebrow="Special Rates"
          title="Serving those who serve."
          sub="We offer preferred pricing for the places that hold a community together — because protecting them is a calling, not just a contract."
          align="center"
          testid="special-rates-heading"
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-14">
          {[
            { icon: Church, t: "Churches", d: "Sanctuaries, fellowship halls, and campuses kept secure with respect for your mission and your budget." },
            { icon: GraduationCap, t: "Schools", d: "Public and private schools — entry control, cameras, and intercoms serviced so students stay safe." },
            { icon: Siren, t: "Emergency Personnel Facilities", d: "Fire halls, EMS stations, and dispatch facilities get priority response and preferred rates." },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.1}>
              <div data-testid={`rates-card-${i}`} className="text-center bg-[#121216] border border-gold/15 rounded-sm p-8 gold-glow-card h-full">
                <div className="w-14 h-14 mx-auto flex items-center justify-center border border-gold/40 bg-gold/5 rounded-full">
                  <c.icon size={24} className="text-gold" />
                </div>
                <h3 className="font-display text-lg font-semibold text-gold-champagne/95 mt-5">{c.t}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mt-3">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="service-area-section" className="border-y border-gold/15 bg-[#0D0D10] relative overflow-hidden">
        <div className="absolute inset-0 spotlight pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading
              eyebrow="Service Area"
              title="Proudly serving Middle Tennessee."
              sub="Based in Christiana, dispatched across the region — if you're in Middle Tennessee, you're in our footprint."
              testid="service-area-heading"
            />
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-2.5 mt-9">
                {["Christiana — Home Base", "Murfreesboro", "Shelbyville", "Smyrna", "Rockvale", "Bell Buckle", "Eagleville", "Chapel Hill", "Unionville", "Fosterville", "Rutherford County", "Bedford County"].map((t) => (
                  <span key={t} className="font-mono-label text-[11px] uppercase tracking-[0.16em] border border-gold/25 bg-gold/5 text-zinc-300 px-3.5 py-2 rounded-sm">
                    {t}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-base text-zinc-200 border border-gold/30 bg-gold/5 rounded-sm px-6 py-5">
                Outside the area? <a href={PHONE_TEL} data-testid="service-area-call-link" className="text-gold font-semibold hover:text-gold-champagne transition-colors">Call {PHONE}</a> — for the right project, we travel.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="relative rounded-sm border border-gold/25 overflow-hidden gold-glow-card">
              <iframe
                title="Redeemer Solutions LLC service area map — Christiana, TN and Middle Tennessee"
                data-testid="service-area-map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-87.15%2C35.30%2C-85.70%2C36.05&layer=mapnik&marker=35.7114%2C-86.3967"
                className="w-full h-[420px]"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#0A0A0C] to-transparent pointer-events-none" />
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand title={SLOGAN} />
    </div>
  );
}
